import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableToken,
  useCollectionBinding,
  useContentToken,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { Play } from 'lucide-react';
import { clean, hasValue } from '../utils/content';

interface Tour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
  virtual_tour_thumbnail?: string | null;
  virtual_tour_description?: string | null;
}

/** Featured tour embed with a thumbnail rail for switching between tours. */
export function VirtualTours() {
  const tours = useCollectionBinding<Tour>('virtualtours.items', { collection: 'virtual_tours', defaultLimit: 24 });
  const emptyText = useContentToken('virtualtours.empty_text');
  const list = (tours || []).filter((t) => hasValue(t.virtual_tour_url));
  const [active, setActive] = useState(0);
  const current = list[Math.min(active, Math.max(list.length - 1, 0))];

  return (
    <section className="bg-background pb-16 pt-6 md:pb-20">
      <div className="mx-auto max-w-[1180px] px-4 sm:px-6 lg:px-10">
        <EditableCollectionBinding bindingPath="virtualtours.items" label="Virtual Tours">
          {!current ? (
            <EditableToken path="virtualtours.empty_text" as="p" className="js-copy py-10 text-center text-[color:var(--vibe-color-text-muted)]">
              {emptyText}
            </EditableToken>
          ) : (
            <>
              <div key={current.virtual_tour_url} className="js-enter-zoom aspect-video w-full overflow-hidden bg-secondary shadow-lg">
                <iframe
                  key={current.virtual_tour_url}
                  src={current.virtual_tour_url || ''}
                  title={clean(current.virtual_tour_title) || 'Virtual tour'}
                  className="h-full w-full border-0"
                  allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div key={`${current.virtual_tour_url}-meta`} className="js-enter js-d1 mt-4 text-center">
                <h2 className="js-heading text-[24px] text-foreground sm:text-[28px]">{clean(current.virtual_tour_title)}</h2>
                {hasValue(current.virtual_tour_description) && (
                  <p className="js-copy mt-1 text-[color:var(--vibe-color-text-muted)]">{current.virtual_tour_description}</p>
                )}
              </div>

              {list.length > 1 && (
                <ul className="js-stagger mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                  {list.map((t, i) => (
                    <li key={`${t.virtual_tour_url}-${i}`}>
                      <button
                        type="button"
                        onClick={() => setActive(i)}
                        data-vibesite-event="virtual_tour_clicked"
                        aria-pressed={i === active}
                        className={cn(
                          'group block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                        )}
                      >
                        <span className={cn('js-zoom relative block aspect-[4/3] overflow-hidden bg-muted ring-2 ring-transparent transition-[box-shadow,transform] duration-500 ease-out group-hover:-translate-y-1', i === active && 'ring-primary')}>
                          {hasValue(t.virtual_tour_thumbnail) ? (
                            <VibesitesImage url={t.virtual_tour_thumbnail} alt="" sizes="(min-width: 1024px) 220px, 45vw" className="h-full w-full object-cover" />
                          ) : null}
                          <span className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-90 transition-colors duration-500 group-hover:bg-black/40">
                            <Play className="h-9 w-9 text-white drop-shadow transition-transform duration-500 ease-out group-hover:scale-125" aria-hidden />
                          </span>
                        </span>
                        <span className="mt-2 block font-body text-[15px] font-semibold uppercase tracking-wide text-foreground">
                          {clean(t.virtual_tour_title) || `Tour ${i + 1}`}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </EditableCollectionBinding>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTours',
  displayName: 'Virtual Tours',
  tokens: {
    'virtualtours.empty_text': {
      label: 'No Tours Message',
      type: 'text',
      defaultValue: 'Virtual tours are coming soon. Schedule a visit to see our homes in person.',
    },
  },
  collectionBindings: {
    'virtualtours.items': { label: 'Virtual Tours', collection: 'virtual_tours', defaultLimit: 24 },
  },
};

(VirtualTours as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTours;
